import {match} from 'ts-pattern';

import {createElement, useEffect, useState} from 'react';

import {always} from 'ramda';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {useListPermissionExpressionQuery} from '@dms/api/security';
import {Module, ModuleItem, Register} from '@dms/api/shared';
import {notificationTypes, register} from '@dms/shared';

import {isRunningInCypress} from 'shared/web';

import {Navigator} from '../../components/Navigator/Navigator';
import {resolveFeatureFlag} from './utils/resolveFeatureFlag';
import {resolvePermission} from './utils/resolvePermission';
import {resolvePermissionAtLeastOne} from './utils/resolvePermissionAtLeastOne';

/**
 * This hook needs to be called after correctly initializing Flagsmith via useInitFlagsmith() hook.
 * Flagsmith init is in Initialize.tsx since we need to initialize feature flags in the uppermost
 * boundary where possible. This hook will not work correctly if flagsmith is not initialized
 * previously. If encountering errors, please double-check if flagsmith is initialized before
 * calling this hook.
 */
export function useRegisterModules(modules: Module[]) {
  const {data: userPermissions, refetch: refetchUserPermissions} =
    useListPermissionExpressionQuery();

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type === notificationTypes.PERMISSION_CHANGED) {
      refetchUserPermissions();
    }
  });

  const isAccessible = (module: Module | ModuleItem) => {
    const permission = module.requiredPermission;
    const permissionAtLeastOne = module.requiredPermissionAtLeastOne;
    const featureFlag = module.requiredFeatureFlag;

    const hasPermission = resolvePermission(permission, userPermissions);
    const hasPermissionAtLeastOne = resolvePermissionAtLeastOne(
      permissionAtLeastOne,
      userPermissions
    );
    const isFeatureFlagEnabled = resolveFeatureFlag(featureFlag);

    const isModuleAccessible = match([hasPermission, hasPermissionAtLeastOne, isFeatureFlagEnabled])
      .with([true, true, true], always(true))
      .otherwise(always(false));

    return isModuleAccessible;
  };

  const [modulesCache, cache] = useState<Register | null>(null);

  useEffect(() => {
    if (!modules || !userPermissions) {
      return;
    }

    Promise.all(modules).then(
      (result) => {
        cache(register(result, isAccessible, createElement(Navigator), isRunningInCypress()));
      },
      () => {
        throw Error('Application cannot load modules. Please check the validity of each module.');
      }
    );

    return () => {
      cache(null);
    };
  }, [userPermissions]);

  return modulesCache;
}
